import React, {useEffect, useMemo, useState} from 'react';
import {NavLink, useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {
    Badge,
    Button,
    ButtonGroup,
    ListGroup,
    ListGroupItem,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import OrganizationCard from '../../components/OrganizationCard';
import FiltersLabel from '../../components/FiltersLabel';
import Loading from '../../components/Loading';
import PageHeader from '../../components/PageHeader';
import TierBadge from '../../components/TierBadge';
import {getSystemStatus, SYNC_INTERVAL_MS} from '../../components/StatusIcon';
import {fetchOrganizations} from '../../slices/organizations.slice';
import {countryName} from '../../utils/countries';

const time = value => (value ? new Date(value).getTime() : 0);

// `GET /organizations` returns every site in one unpaginated response, so the
// filtering and sorting below stay in the browser rather than round-tripping.
const SORT_OPTIONS = [
    {
        key: 'updated',
        label: 'Recently updated',
        icon: 'fa-clock-rotate-left',
        description: 'Last edit to the organization, newest first',
        compare: (a, b) => time(b.updatedAt) - time(a.updatedAt),
    },
    {
        key: 'created',
        label: 'Recently added',
        icon: 'fa-calendar-plus',
        description: 'Newest organizations first',
        compare: (a, b) => time(b.createdAt) - time(a.createdAt),
    },
    {
        key: 'name-asc',
        label: 'Name A–Z',
        icon: 'fa-arrow-down-a-z',
        description: 'Alphabetical by name',
        compare: (a, b) => (a.name || '').localeCompare(b.name || ''),
    },
    {
        key: 'name-desc',
        label: 'Name Z–A',
        icon: 'fa-arrow-down-z-a',
        description: 'Reverse alphabetical by name',
        compare: (a, b) => (b.name || '').localeCompare(a.name || ''),
    },
    {
        // Oldest sync first: a site that stopped reporting is the one that needs
        // attention, so it belongs at the top rather than the bottom.
        key: 'stalest',
        label: 'Longest without sync',
        icon: 'fa-rotate',
        description: 'Sites that have not reported in, first',
        compare: (a, b) => time(a.lastSynced) - time(b.lastSynced),
    },
    {
        key: 'credits',
        label: 'Most credits',
        icon: 'fa-coins',
        description: 'Highest credit balance first',
        compare: (a, b) => (b.creditAmount || 0) - (a.creditAmount || 0),
    },
];

const DEFAULT_SORT = SORT_OPTIONS[0].key;

const TIERS = ['premium', 'mid'];

// Any of the three monitored systems being down makes a site "offline".
const isOffline = organization => Object.values(getSystemStatus(organization)).some(up => !up);

// A site that hasn't reported within the cronjob's interval: whatever status it
// shows is a last-known value, not a live one.
const isStale = organization => {
    const syncedAt = time(organization.lastSynced);
    return !syncedAt || Date.now() - syncedAt > SYNC_INTERVAL_MS;
};

const Organizations = () => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const token = useSelector(state => state.auth.token);
    const organizations = useSelector(state => state.organizations.list);
    const status = useSelector(state => state.organizations.status);
    const error = useSelector(state => state.organizations.error);

    const parseURLFilters = () => {
        const sortParam = searchParams.get('sort');
        const countriesParam = searchParams.get('countries');
        const tiersParam = searchParams.get('tiers');
        const yachtParam = searchParams.get('yacht');

        return {
            search: searchParams.get('search') || '',
            sort: SORT_OPTIONS.some(({ key }) => key === sortParam) ? sortParam : DEFAULT_SORT,
            countries: countriesParam ? countriesParam.split(',') : [],
            tiers: tiersParam ? tiersParam.split(',') : [],
            isYacht: yachtParam === 'true' ? true : yachtParam === 'false' ? false : undefined,
            offline: searchParams.get('offline') === 'true',
            stale: searchParams.get('stale') === 'true',
        };
    };

    const [filterBy, setFilterBy] = useState(parseURLFilters());
    const [filterModal, setFilterModal] = useState({ isOpen: false, filter: '' });

    const toggleFilterModal = filter => {
        setFilterModal({ isOpen: !filterModal.isOpen, filter });
    };

    const updateURLParams = (newFilters) => {
        const params = new URLSearchParams();

        if (newFilters.search) {
            params.set('search', newFilters.search);
        }
        if (newFilters.sort !== DEFAULT_SORT) {
            params.set('sort', newFilters.sort);
        }
        if (newFilters.countries.length > 0) {
            params.set('countries', newFilters.countries.join(','));
        }
        if (newFilters.tiers.length > 0) {
            params.set('tiers', newFilters.tiers.join(','));
        }
        if (newFilters.isYacht !== undefined) {
            params.set('yacht', newFilters.isYacht);
        }
        if (newFilters.offline) {
            params.set('offline', 'true');
        }
        if (newFilters.stale) {
            params.set('stale', 'true');
        }

        setSearchParams(params);
    };

    const applyFilters = (newFilters) => {
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    const toggleFilter = (filterKey, value) => {
        const selected = filterBy[filterKey];
        applyFilters({
            ...filterBy,
            [filterKey]: selected.includes(value)
                ? selected.filter(item => item !== value)
                : [...selected, value],
        });
    };

    // Yacht cycles through all → yachts only → cinemas only, the same three
    // states the PIN/2FA chips use on the users list.
    const toggleYacht = () => {
        applyFilters({
            ...filterBy,
            isYacht: filterBy.isYacht === undefined ? true : filterBy.isYacht === true ? false : undefined,
        });
    };

    const toggleFlag = (filterKey) => {
        applyFilters({ ...filterBy, [filterKey]: !filterBy[filterKey] });
    };

    const handleSearch = event => {
        event.preventDefault();
        applyFilters({ ...filterBy, search: event.target.value });
    };

    const selectSort = (sort) => {
        applyFilters({ ...filterBy, sort });
        setFilterModal({ isOpen: false, filter: '' });
    };

    const areFiltersActive = useMemo(() => {
        const { search, countries, tiers, isYacht, offline, stale } = filterBy;

        return (
            search.length > 0 ||
            countries.length > 0 ||
            tiers.length > 0 ||
            isYacht !== undefined ||
            offline ||
            stale
        );
    }, [filterBy]);

    // The chosen ordering survives "Clear all": it says how the list reads, not
    // which organizations it holds.
    const clearFilters = () => {
        applyFilters({
            search: '',
            sort: filterBy.sort,
            countries: [],
            tiers: [],
            isYacht: undefined,
            offline: false,
            stale: false,
        });
    };

    // Only the territories actually present in the list are worth offering.
    const availableCountries = useMemo(() => (
        [...new Set(organizations.map(({ country }) => country).filter(Boolean))]
            .map(code => ({ code, name: countryName(code) }))
            .sort((a, b) => a.name.localeCompare(b.name))
    ), [organizations]);

    const activeSort = SORT_OPTIONS.find(({ key }) => key === filterBy.sort) || SORT_OPTIONS[0];

    const visibleOrganizations = useMemo(() => {
        const search = filterBy.search.trim().toLowerCase();

        return organizations
            .filter(organization => (
                (!search || (organization.name || '').toLowerCase().includes(search)) &&
                (!filterBy.countries.length || filterBy.countries.includes(organization.country)) &&
                (!filterBy.tiers.length || filterBy.tiers.includes(organization.tier)) &&
                (filterBy.isYacht === undefined || Boolean(organization.isYacht) === filterBy.isYacht) &&
                (!filterBy.offline || isOffline(organization)) &&
                (!filterBy.stale || isStale(organization))
            ))
            .sort(activeSort.compare);
    }, [organizations, filterBy, activeSort]);

    useEffect(() => {
        dispatch(fetchOrganizations(token));
    }, [token, dispatch]);

    return (
        <div className='organizations-view page px-4 py-3 d-flex flex-column overflow-y-auto' >
            <PageHeader
                title='organizations'
                addButtonAction={{navigateTo: "/organizations/new"}}
                searchBar={{searchValue: filterBy.search, handleSearch}}
                />
            <div className='contentContainer'>
                <div className="d-flex justify-content-start align-items-center gap-3">
                    <FiltersLabel active={areFiltersActive} onClear={clearFilters}/>

                    <ButtonGroup className="gap-2">
                        <Button color="primary" outline size="sm" className="rounded-3" onClick={() => toggleFilterModal('territories')}>
                            {filterBy.countries.length > 0 ? (
                                <Badge color="success" className="me-2 text-white">
                                    {filterBy.countries.length}
                                </Badge>
                            ) : (
                                <span className="fas fa-map-marker-alt me-2"/>
                            )}
                            Territory
                        </Button>

                        <Button color="primary" outline size="sm" className="rounded-3" onClick={() => toggleFilterModal('tier')}>
                            {filterBy.tiers.length > 0 ? (
                                <Badge color="success" className="me-2 text-white">
                                    {filterBy.tiers.length}
                                </Badge>
                            ) : (
                                <span className="fas fa-gem me-2"/>
                            )}
                            Tier
                        </Button>

                        <Button
                            color="primary"
                            outline={filterBy.isYacht === undefined}
                            size="sm"
                            className="rounded-3"
                            onClick={toggleYacht}
                        >
                            {filterBy.isYacht === true ? (
                                <span className="fas fa-toggle-on me-2 text-success"/>
                            ) : filterBy.isYacht === false ? (
                                <span className="fas fa-toggle-off me-2 text-danger"/>
                            ) : (
                                <span className="fas fa-ship me-2"/>
                            )}
                            Yacht
                        </Button>

                        <Button
                            color="primary"
                            outline={!filterBy.offline}
                            size="sm"
                            className="rounded-3"
                            onClick={() => toggleFlag('offline')}
                        >
                            {filterBy.offline ? (
                                <span className="fas fa-toggle-on me-2 text-success"/>
                            ) : (
                                <span className="fas fa-plug-circle-xmark me-2"/>
                            )}
                            Offline
                        </Button>

                        <Button
                            color="primary"
                            outline={!filterBy.stale}
                            size="sm"
                            className="rounded-3"
                            onClick={() => toggleFlag('stale')}
                        >
                            {filterBy.stale ? (
                                <span className="fas fa-toggle-on me-2 text-success"/>
                            ) : (
                                <span className="fas fa-triangle-exclamation me-2"/>
                            )}
                            Not Synced
                        </Button>
                    </ButtonGroup>

                    <div className="flex-grow-1">
                        <span className="fas fa-list me-2 text-primary" />
                        showing {visibleOrganizations.length} result{visibleOrganizations.length === 1 ? '' : 's'}
                    </div>

                    <Button
                        color="primary"
                        outline={filterBy.sort === DEFAULT_SORT}
                        size="sm"
                        className="rounded-3 text-nowrap"
                        onClick={() => toggleFilterModal('sort')}
                    >
                        <span className={`fas ${activeSort.icon} me-2`}/>
                        {activeSort.label}
                    </Button>
                </div>

                {status === 'loading' && <Loading length={3} />}

                {status === 'success' && visibleOrganizations.map((organization) => (
                    <NavLink to={`/organizations/${organization._id}`} key={organization._id} className="text-decoration-none text-white">
                        <OrganizationCard organization={organization} />
                    </NavLink>
                ))}

                {status === 'success' && visibleOrganizations.length === 0 && (
                    <div className="d-flex flex-column align-items-center py-5">
                        <span className="fas fa-inbox fa-3x text-white-50 mb-3 mt-5 pt-5" />
                        {areFiltersActive ? (
                            <>
                                <p className="text-white-50 mb-3">No organizations match your filters</p>
                                <Button color="primary" outline onClick={clearFilters}>
                                    Clear filters
                                </Button>
                            </>
                        ) : (
                            <p className="text-white-50 text-uppercase">No organizations yet</p>
                        )}
                    </div>
                )}

                {status === 'failed' && (
                    <div className="alert alert-danger">{error}</div>
                )}

                <div className="clearfix py-4" />
            </div>

            <Modal isOpen={filterModal.isOpen} toggle={() => toggleFilterModal()} size="md" scrollable>
                <ModalHeader toggle={() => toggleFilterModal()}>
                    {filterModal.filter === 'sort' ? 'Sort organizations' : `Filter by ${filterModal.filter}`}
                </ModalHeader>
                <ModalBody>

                    {filterModal.filter === 'sort' && (
                        <div className="sort-filter-modal">
                            <ListGroup>
                                {SORT_OPTIONS.map(({ key, label, icon, description }) => (
                                    <ListGroupItem
                                        onClick={() => selectSort(key)}
                                        key={key}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.sort === key}
                                        className="border-0 border-start border-primary border-2 position-relative
                                            bg-broken text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <span className={`fas fa-fw ${icon} text-primary`}/>
                                        <div className="flex-grow-1">
                                            {label}
                                            <div className="small text-white-50">{description}</div>
                                        </div>
                                        {filterBy.sort === key && (
                                            <span className="fas fa-check-circle text-success"/>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'territories' && (
                        <div className="territories-filter-modal">
                            {availableCountries.length === 0 && (
                                <div className="text-white-50">No territories set on any organization yet</div>
                            )}
                            <ListGroup>
                                {availableCountries.map(({ code, name }) => (
                                    <ListGroupItem
                                        onClick={() => toggleFilter('countries', code)}
                                        key={code}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.countries.includes(code)}
                                        className="border-0 border-start border-primary border-2 position-relative
                                            bg-broken text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <span className="fas fa-fw fa-map-marker-alt text-primary"/>
                                        <div className="flex-grow-1">{name}</div>
                                        {filterBy.countries.includes(code) && (
                                            <span className="fas fa-check-circle text-success"/>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'tier' && (
                        <div className="tier-filter-modal">
                            <ListGroup>
                                {TIERS.map(tier => (
                                    <ListGroupItem
                                        onClick={() => toggleFilter('tiers', tier)}
                                        key={tier}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.tiers.includes(tier)}
                                        className="border-0 border-start border-primary border-2 position-relative
                                            bg-broken text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <TierBadge tier={tier} small/>
                                        <div className="flex-grow-1"/>
                                        {filterBy.tiers.includes(tier) && (
                                            <span className="fas fa-check-circle text-success"/>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => toggleFilterModal()}>
                        <span className="fas fa-check-circle me-2" />
                        Show results
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default Organizations;
